import { useState } from 'react';
import { ButtonWithDropdown } from '@simplywallst/ui-core';
import MoreHorizontalSolid from '@simplywallst/ui-core/icons/MoreHorizontalSolid';
import { useTypeformDrawer } from '@/hooks/useTypeformDrawer';
import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';
import { useOverviewTranslation } from '@/pages/CompanyReport/components/OverviewSection/hooks/useOverviewTranslation';

const GuestRegistrationModal = loadable(
  () => import(/* webpackPrefetch: true */ '@components/GuestRegistrationModal')
);

interface MoreProps {
  cyId?: string;
  userId: string;
  companyName: string;
  feedbackLabel: string;
  exportLightUrl?: string;
  exportDarkUrl?: string | null;
  shouldShowRegoModal: boolean;
}

const FEEDBACK = 'FEEDBACK';
const EXPORT_LIGHT = 'EXPORT_LIGHT';
const EXPORT_DARK = 'EXPORT_DARK';

const More = ({
  cyId,
  companyName,
  feedbackLabel,
  exportLightUrl,
  exportDarkUrl,
  shouldShowRegoModal,
}: MoreProps) => {
  const makeTypeformPopup = useTypeformDrawer('Ll9OXh', {
    section: feedbackLabel,
    company: companyName,
  });
  const [openRegoModal, setOpenRegoModal] = useState(false);
  const { trackEvent } = useTracking({
    type: 'track',
    subject: 'inlineRegistration',
  });

  const t = useOverviewTranslation();

  const openLink = async (link?: string | null) => {
    if (RUNTIME_ENV !== 'server' && link) {
      if (RUNTIME_ENV === 'native') {
        try {
          const { Browser } = await import('@capacitor/browser');
          Browser.open({ url: link });
        } catch (error) {
          console.log('Capacitor Browser error', error);
        }
      } else {
        window.open(link, '_blank');
      }
    }
  };

  const getOptions = () => {
    const actionLabels: { label: string; value: string }[] = [];
    if (feedbackLabel && feedbackLabel.length > 0)
      actionLabels.push({
        label: t('actions.feedback'),
        value: FEEDBACK,
      });
    if (exportLightUrl && exportLightUrl) {
      actionLabels.push({
        label: t('actions.downloadLight'),
        value: EXPORT_LIGHT,
      });
      actionLabels.push({
        label: t('actions.downloadDark'),
        value: EXPORT_DARK,
      });
    } else if (exportLightUrl) {
      actionLabels.push({
        label: t('actions.download'),
        value: EXPORT_LIGHT,
      });
    } else if (exportDarkUrl) {
      actionLabels.push({
        label: t('actions.download'),
        value: EXPORT_DARK,
      });
    }
    return actionLabels;
  };

  const handleMoreActionSelect = async (value: string) => {
    switch (value) {
      case FEEDBACK:
        makeTypeformPopup();
        break;
      case EXPORT_LIGHT:
        shouldShowRegoModal ? setOpenRegoModal(true) : openLink(exportLightUrl);
        break;
      case EXPORT_DARK:
        shouldShowRegoModal ? setOpenRegoModal(true) : openLink(exportDarkUrl);
        break;
    }
  };
  return (
    <>
      <ButtonWithDropdown
        name="more"
        styleType="gentle"
        data-cy-id={cyId}
        options={getOptions()}
        onSelect={handleMoreActionSelect}
        marginRight
      >
        <MoreHorizontalSolid />
      </ButtonWithDropdown>
      {openRegoModal && (
        <GuestRegistrationModal
          canClose={true}
          onClose={() => {
            setOpenRegoModal(false);
            trackEvent({
              action: 'close',
              modifier: 'log_in_sign_up',
            });
          }}
          onRegisterSuccess={() => {
            setOpenRegoModal(false);
            trackEvent({
              modifier: 'registration',
              action: 'success',
            });
          }}
          onLoginSuccess={() => {
            setOpenRegoModal(false);
            trackEvent({
              modifier: 'login',
              action: 'success',
            });
          }}
        />
      )}
    </>
  );
};

export default More;
